import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Check,
  Sparkles,
  ShieldCheck,
  Zap,
  Users,
  Boxes,
  FileText,
  Activity,
  ArrowRight,
  ArrowLeft,
  CreditCard,
  CheckCircle2,
  Lock,
  Building2,
  ChevronRight,
  ChevronLeft,
  X,
  Send,
  Star,
  Layers,
} from "lucide-react";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import { toast } from "react-toastify";
import swal from "sweetalert";
import { getTenantInfo } from "../../utils/domain";

export default function TenantPlans() {
  const navigate = useNavigate();
  const reduxUser = useSelector((state) => state.user);
  const tenantInfo = getTenantInfo();

  const [slideIndex, setSlideIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [loading, setLoading] = useState(true);

  // Subscription & Plans Data
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [pendingRequest, setPendingRequest] = useState(null);

  // Modal State
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [changeNotes, setChangeNotes] = useState(
    "Requesting upgrade for multi-user access and QuickBooks Desktop sync."
  );
  const [submitting, setSubmitting] = useState(false);

  // 1. Fetch Subscription & Public Plans
  const fetchSubscriptionData = useCallback(async () => {
    loader(true);
    setLoading(true);

    try {
      // Endpoint 1: GET tenants/portal/subscription
      const subRes = await ApiClient.get("tenants/portal/subscription", {}, "", true);
      if (subRes?.success && (subRes?.data || subRes?.subscription)) {
        setSubscription(subRes.data || subRes.subscription);
      } else if (subRes && typeof subRes === "object" && !subRes.error) {
        setSubscription(subRes);
      }

      // Endpoint 2: GET tenants/portal/subscription/plans
      const plansRes = await ApiClient.get("tenants/portal/subscription/plans", {}, "", true);
      if (plansRes?.success && (plansRes?.data || plansRes?.plans)) {
        const fetchedPlans = plansRes.data?.plans || plansRes.data || plansRes.plans;
        if (Array.isArray(fetchedPlans) && fetchedPlans.length > 0) {
          setPlans(fetchedPlans);
        }
      } else if (Array.isArray(plansRes)) {
        setPlans(plansRes);
      }
    } catch (err) {
      console.warn("Failed to load subscription API:", err);
    } finally {
      loader(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token && !reduxUser?._id && !reduxUser?.id) {
      navigate("/login");
      return;
    }
    fetchSubscriptionData();
  }, [fetchSubscriptionData, navigate, reduxUser]);

  // Fallback / Normalized Data
  const currentPlan =
    subscription?.plan ||
    subscription?.current_plan ||
    subscription?.active_plan || {
      _id: "plan-desktop-pro",
      id: "plan-desktop-pro",
      name: "Desktop Pro",
      code: "desktop_pro",
      price: 49,
      billing_cycle: "monthly",
    };

  const currentPlanId = currentPlan?._id || currentPlan?.id || "plan-desktop-pro";

  // Quotas / Utilization Meters
  const quotas =
    subscription?.quota_meters ||
    subscription?.quotas ||
    subscription?.meters || {
      team_members: {
        current: subscription?.team_members_count || 1,
        max: subscription?.team_members_limit || 5,
        unit: "seats",
      },
      monthly_quotes: {
        current: subscription?.monthly_quotes_count || 0,
        max: subscription?.monthly_quotes_limit || 300,
        unit: "quotes",
      },
      catalog_items: {
        current: subscription?.catalog_items_count || 0,
        max: subscription?.catalog_items_limit || 5000,
        unit: "SKUs",
      },
    };

  // Feature Entitlements
  const entitlements =
    subscription?.feature_entitlements ||
    subscription?.entitlements ||
    subscription?.features || {
      quickbooks_sync: true,
      custom_domain: false,
      multi_user_rbac: true,
      dedicated_storefront: true,
      ai_quote_generator: true,
      priority_support: false,
    };

  // Normalized Plans Matrix
  const availablePlans =
    plans.length > 0
      ? plans
      : [
        {
          _id: "plan-starter",
          id: "plan-starter",
          name: "Starter Tier",
          tagline: "Essential tools for emerging dealers getting started",
          price_monthly: 29,
          price_annual: 24,
          tier: "starter",
          quotas: {
            team_members: 2,
            monthly_quotes: 100,
            catalog_items: 1000,
          },
          features: [
            "Up to 2 Admin & Sales Rep Seats",
            "100 Monthly Quoting Inquiries",
            "1,000 Catalog Equipment SKUs",
            "Standard Digital Storefront",
            "Automated Email PDF Quotes",
            "Standard Business Hours Support",
          ],
        },
        {
          _id: "plan-desktop-pro",
          id: "plan-desktop-pro",
          name: "Desktop Pro",
          tagline: "High-performance quoting with bidirectional QuickBooks synchronization",
          price_monthly: 49,
          price_annual: 39,
          is_popular: true,
          tier: "professional",
          quotas: {
            team_members: 5,
            monthly_quotes: 300,
            catalog_items: 5000,
          },
          features: [
            "Up to 5 Team Member Seats",
            "300 Monthly Quote Generations",
            "5,000 Active Catalog SKUs",
            "Live QuickBooks Sync Engine",
            "Multi-User Role Permissions (RBAC)",
            "AI Product Specification Parsing",
            "Dedicated Tenant Subdomain Portal",
            "Priority Customer Care SLA",
          ],
        },
        {
          _id: "plan-enterprise",
          id: "plan-enterprise",
          name: "Enterprise Tier",
          tagline: "Unlimited scale with custom domains and dedicated SLA",
          price_monthly: 199,
          price_annual: 159,
          tier: "enterprise",
          quotas: {
            team_members: 25,
            monthly_quotes: 2500,
            catalog_items: 50000,
          },
          features: [
            "Up to 25 Team Member Seats",
            "2,500+ Monthly Quotes & Orders",
            "50,000 Equipment Catalog SKUs",
            "Custom Whitelabel Domain & SSL",
            "Priority 24/7 Dedicated SLA Support",
            "Dedicated Account Executive",
            "Advanced Real-Time Activity Audit Logs",
            "Custom Contract Billing & Terms",
          ],
        },
      ];

  // Helper: Extract Plan Features safely
  const getPlanFeatures = (targetPlan) => {
    if (!targetPlan) return [];
    if (Array.isArray(targetPlan.features)) {
      return targetPlan.features
        .map((f) => (typeof f === "object" ? f.name || f.title || f.feature || (typeof f.label === "string" ? f.label : null) : String(f)))
        .filter(Boolean);
    }
    if (Array.isArray(targetPlan.feature_list)) {
      return targetPlan.feature_list
        .map((f) => (typeof f === "object" ? f.name || f.title || f.feature || (typeof f.label === "string" ? f.label : null) : String(f)))
        .filter(Boolean);
    }
    if (targetPlan.features && typeof targetPlan.features === "object") {
      return Object.entries(targetPlan.features)
        .filter(([key, val]) => val !== false && val !== null && val !== undefined && typeof val !== "object" && !key.toLowerCase().includes("flag"))
        .map(([key, val]) => {
          let formatted = key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
          if (formatted.toLowerCase().includes("quickbooks")) {
            formatted = formatted.replace(/Quickbooks/gi, "QuickBooks");
          }
          if (formatted.toLowerCase().includes("ai ")) {
            formatted = formatted.replace(/Ai /gi, "AI ");
          }
          return typeof val === "boolean" ? formatted : `${formatted}: ${val}`;
        })
        .filter(Boolean);
    }
    if (typeof targetPlan.features === "string") {
      return targetPlan.features.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    }
    return [
      "Standard Equipment Quoting Portal",
      "Customer Inquiries & Estimating",
      "Digital Catalog Management",
    ];
  };

  // Helper: Extract quota values from plan objects
  const getPlanQuota = (plan, key, fallback) => {
    if (!plan) return fallback;
    if (plan.quotas && plan.quotas[key] !== undefined) {
      const v = plan.quotas[key];
      return typeof v === "object" ? (v.limit ?? v.max ?? fallback) : v;
    }
    if (plan[key] !== undefined) return plan[key];
    if (key === "team_members") {
      return plan.team_members_limit || plan.team_members || plan.seats || plan.max_users || fallback;
    }
    if (key === "monthly_quotes") {
      return plan.monthly_quotes_limit || plan.monthly_quotes || plan.quotes_limit || plan.quotes || fallback;
    }
    if (key === "catalog_items") {
      return plan.catalog_items_limit || plan.catalog_items || plan.items_limit || plan.skus || fallback;
    }
    return fallback;
  };

  // Helper: Format quota numbers safely
  const getQuotaDisplay = (val, fallback = "0") => {
    if (val === undefined || val === null) return fallback;
    if (typeof val === "object") return val.limit ?? val.max ?? fallback;
    if (typeof val === "string" && (val.toLowerCase() === "unlimited" || val === "∞")) return "Unlimited";
    if (val === -1 || val >= 999999) return "Unlimited";
    return typeof val === "number" ? val.toLocaleString() : String(val);
  };

  // Percentage helper
  const getPercent = (curr = 0, max = 1) => {
    if (!max || max <= 0) return 0;
    return Math.min(100, Math.round((curr / max) * 100));
  };

  // Submit Plan Change Request
  const handleSubmitChangeRequest = async (e) => {
    e.preventDefault();
    if (!selectedPlan) return;

    setSubmitting(true);
    loader(true);

    try {
      // Endpoint 3: POST tenants/portal/subscription/change-request
      const targetPlanId = selectedPlan?._id || selectedPlan?.id;
      const payload = {
        targetPlanId: String(targetPlanId),
        notes: changeNotes.trim(),
      };

      const res = await ApiClient.post(
        "tenants/portal/subscription/change-request",
        payload,
        "",
        true
      );

      loader(false);
      setSubmitting(false);

      if (res?.success || res?.status === "success" || !res?.error) {
        setPendingRequest({
          targetPlan: selectedPlan,
          submittedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          notes: changeNotes,
        });

        setSelectedPlan(null);

        swal({
          title: "Request Submitted!",
          text: `Your change request to switch to ${selectedPlan.name} has been submitted for administrative confirmation.`,
          icon: "success",
          buttons: {
            confirm: {
              text: "Done",
              className: "bg-[#115B9D] text-white px-6 py-2 rounded-xl font-bold",
            },
          },
        });
      } else {
        const errorMsg =
          res?.error?.message ||
          res?.message ||
          "Failed to submit plan change request. Please try again.";
        toast.error(errorMsg);
      }
    } catch (err) {
      loader(false);
      setSubmitting(false);
      console.error("Change request error:", err);
      toast.error("An error occurred while submitting your change request.");
    }
  };

  const isCurrentPlanMatch = (plan) => {
    if (!plan) return false;
    const planId = plan._id || plan.id;
    const curId = currentPlan?._id || currentPlan?.id;
    if (planId && curId && String(planId) === String(curId)) return true;
    if (plan.code && currentPlan?.code && String(plan.code).toLowerCase() === String(currentPlan.code).toLowerCase()) return true;
    if (plan.name && currentPlan?.name && String(plan.name).toLowerCase().trim() === String(currentPlan.name).toLowerCase().trim()) return true;
    if (plan.is_current === true || plan.current === true) return true;
    return false;
  };

  return (
    <>
      <Header />
      <div className="min-h-[calc(100vh_-_60px)] md:min-h-[calc(100vh_-_70px)] mt-[60px] lg:mt-[70px] bg-[#F4F7FB] py-6 lg:py-8">
        <div className="container mx-auto px-3 sm:px-6 lg:px-8 max-w-5xl space-y-6 sm:space-y-8">
          {/* ======================================================== */}
          {/* TOP BREADCRUMB & HEADER */}
          {/* ======================================================== */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 mb-1">
                <Link
                  to="/tenant/dashboard"
                  className="hover:text-[#115B9D] transition-colors"
                >
                  Dashboard
                </Link>
                <ChevronRight size={13} className="text-slate-400" />
                <span className="text-[#115B9D]">Subscription &amp; Plans</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight flex items-center gap-2.5">
                Subscription &amp; Plans
              </h1>
            </div>

            <div>
              <Link
                to="/tenant/dashboard"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white border border-slate-200 hover:border-[#115B9D] text-slate-700 hover:text-[#115B9D] text-xs sm:text-sm font-semibold transition-all shadow-sm hover:shadow"
              >
                <ArrowLeft size={15} />
                <span>Go to Dashboard</span>
              </Link>
            </div>
          </div>

          {/* Pending Request Alert (if submitted) */}
          {pendingRequest && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-4 flex items-center gap-3.5 shadow-sm">
              <div className="w-8 h-8 rounded-xl bg-[#115B9D] text-white flex items-center justify-center shrink-0">
                <CheckCircle2 size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-bold text-slate-900">
                  Plan Change Request Pending: {pendingRequest.targetPlan?.name}
                </p>
                <p className="text-xs text-slate-600">
                  Submitted at {pendingRequest.submittedAt}. Our team will confirm your quota adjustment shortly.
                </p>
              </div>
            </div>
          )}

          {/* ======================================================== */}
          {/* MODERN CURRENT PLAN HERO CARD */}
          {/* ======================================================== */}
          <div className="rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-50/40 via-white to-sky-50/20 p-5 sm:p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
              <div className="flex items-center gap-4">
                <div className="w-13 h-13 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-[#115B9D] via-[#1571BC] to-[#2590E0] text-white flex items-center justify-center shrink-0 shadow-lg shadow-[#115B9D]/20">
                  <CreditCard size={26} />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                      Current Subscription
                    </span>

                  </div>
                  <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                    {currentPlan.name || "Desktop Pro"}
                  </h2>
                  <p className="text-xs text-slate-500 font-medium mt-0.5">
                    Monthly billing cycle • Renews automatically on the 1st of each month
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 self-start md:self-auto pt-2 md:pt-0 border-t md:border-t-0 border-slate-100">
                <div className="text-left md:text-right">
                  <span className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                    ${currentPlan.price || 49}
                  </span>
                  <span className="text-xs text-slate-500 font-semibold block">/ month</span>
                </div>
                <a
                  href="#plans-section"
                  className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#115B9D] to-[#1E88E5] hover:opacity-95 text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/20 flex items-center gap-1.5"
                >
                  <span>Upgrade Tier</span>
                  <ArrowRight size={13} />
                </a>
              </div>
            </div>
          </div>

          {/* ======================================================== */}
          {/* RESOURCE QUOTA METERS (MODERN WIDGET ROW) */}
          {/* ======================================================== */}
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                <Layers size={14} className="text-[#115B9D]" />
                Resource Quota Utilization
              </span>
              <span className="text-xs font-medium text-slate-500">
                Resets on next monthly cycle
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Widget 1: Team Seats */}
              <div className="bg-white rounded-2xl p-5 border border-slate-200/90 shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-xl bg-blue-50 text-[#115B9D] flex items-center justify-center font-bold">
                    <Users size={18} />
                  </div>
                  <span className="px-2 py-0.5 rounded-md text-[11px] font-bold bg-blue-50 text-[#115B9D]">
                    {(quotas.team_members?.max ?? 5) - (quotas.team_members?.current ?? 1)} Seats Left
                  </span>
                </div>
                <p className="text-xs font-bold text-slate-500">Team Members Quota</p>
                <div className="flex items-baseline gap-1 my-2">
                  <span className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                    {quotas.team_members?.current ?? 1}
                  </span>
                  <span className="text-sm font-semibold text-slate-400">
                    / {quotas.team_members?.max ?? 5} seats
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-[#115B9D] to-[#1E88E5] h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${getPercent(quotas.team_members?.current ?? 1, quotas.team_members?.max ?? 5)}%`,
                    }}
                  />
                </div>
                <div className="flex justify-between items-center mt-2 text-[11px] font-medium text-slate-500">
                  <span>{getPercent(quotas.team_members?.current ?? 1, quotas.team_members?.max ?? 5)}% capacity</span>
                  <span>Active admins &amp; reps</span>
                </div>
              </div>

              {/* Widget 2: Monthly Quotes */}
              <div className="bg-white rounded-2xl p-5 border border-slate-200/90 shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
                    <FileText size={18} />
                  </div>
                  <span className="px-2 py-0.5 rounded-md text-[11px] font-bold bg-indigo-50 text-indigo-700">
                    Resets Monthly
                  </span>
                </div>
                <p className="text-xs font-bold text-slate-500">Monthly Quotes Quota</p>
                <div className="flex items-baseline gap-1 my-2">
                  <span className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                    {quotas.monthly_quotes?.current ?? 0}
                  </span>
                  <span className="text-sm font-semibold text-slate-400">
                    / {quotas.monthly_quotes?.max ?? 300} quotes
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-indigo-500 to-indigo-600 h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${getPercent(quotas.monthly_quotes?.current ?? 0, quotas.monthly_quotes?.max ?? 300)}%`,
                    }}
                  />
                </div>
                <div className="flex justify-between items-center mt-2 text-[11px] font-medium text-slate-500">
                  <span>{getPercent(quotas.monthly_quotes?.current ?? 0, quotas.monthly_quotes?.max ?? 300)}% volume used</span>
                  <span>{(quotas.monthly_quotes?.max ?? 300) - (quotas.monthly_quotes?.current ?? 0)} quotes left</span>
                </div>
              </div>

              {/* Widget 3: Catalog Items */}
              <div className="bg-white rounded-2xl p-5 border border-slate-200/90 shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
                    <Boxes size={18} />
                  </div>
                  <span className="px-2 py-0.5 rounded-md text-[11px] font-bold bg-amber-50 text-amber-800">
                    {quotas.catalog_items?.max ?? 5000} SKUs Limit
                  </span>
                </div>
                <p className="text-xs font-bold text-slate-500">Active Catalog Items</p>
                <div className="flex items-baseline gap-1 my-2">
                  <span className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                    {quotas.catalog_items?.current ?? 0}
                  </span>
                  <span className="text-sm font-semibold text-slate-400">
                    / {(quotas.catalog_items?.max ?? 5000).toLocaleString()} SKUs
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-amber-500 to-orange-500 h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${getPercent(quotas.catalog_items?.current ?? 0, quotas.catalog_items?.max ?? 5000)}%`,
                    }}
                  />
                </div>
                <div className="flex justify-between items-center mt-2 text-[11px] font-medium text-slate-500">
                  <span>{getPercent(quotas.catalog_items?.current ?? 0, quotas.catalog_items?.max ?? 5000)}% storage used</span>
                  <span>{(quotas.catalog_items?.max ?? 5000) - (quotas.catalog_items?.current ?? 0)} SKUs left</span>
                </div>
              </div>
            </div>
          </div>

          {/* ======================================================== */}
          {/* MODERN INLINE FEATURE ENTITLEMENTS STRIP */}
          {/* ======================================================== */}
          <div className="rounded-2xl p-4 sm:p-5 border border-slate-200/90 bg-white shadow-2xs">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-3">
              Current Feature Entitlements
            </span>
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200/80">
                <Check size={13} className="text-emerald-600" />
                QuickBooks Sync
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200/80">
                <Check size={13} className="text-emerald-600" />
                Multi-User RBAC
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200/80">
                <Check size={13} className="text-emerald-600" />
                AI Product Parsing
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200/80">
                <Check size={13} className="text-emerald-600" />
                Storefront Portal
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100/70 text-slate-600 border border-slate-200">
                <Lock size={12} className="text-slate-400" />
                Custom Domain (Enterprise)
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100/70 text-slate-600 border border-slate-200">
                <Lock size={12} className="text-slate-400" />
                Dedicated SLA (Enterprise)
              </span>
            </div>
          </div>

          {/* ======================================================== */}
          {/* COMPACT PRICING SLIDER (SLIDER CAROUSEL) */}
          {/* ======================================================== */}
          {/* ======================================================== */}
          {/* SUBSCRIPTION PLANS (MODERN SLIDER & COMPACT CARDS) */}
          {/* ======================================================== */}
          <div id="plans-section" className="space-y-4 pt-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                  Available Subscription Plans
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Compare plans and choose the ideal tier for your warehouse.
                </p>
              </div>

              <span className="text-xs font-bold text-[#115B9D] bg-blue-50 border border-blue-100 px-3 py-1 rounded-full self-start sm:self-auto">
                Monthly Billing
              </span>
            </div>

            {/* Plan Card Render Helper */}
            {(() => {
              const renderCard = (plan) => {
                const isCurrent = isCurrentPlanMatch(plan);
                const price = plan.price_monthly || plan.price || (isCurrent ? 49 : 99);
                const seats = getQuotaDisplay(getPlanQuota(plan, "team_members", isCurrent ? 5 : 25));
                const quotes = getQuotaDisplay(getPlanQuota(plan, "monthly_quotes", isCurrent ? 300 : 2500));
                const skus = getQuotaDisplay(getPlanQuota(plan, "catalog_items", isCurrent ? 5000 : 50000));
                const features = getPlanFeatures(plan);
                const freeDays = Number(plan.freeDays ?? plan.free_days ?? plan.trial_days ?? plan.trialDays ?? 0);

                return (
                  <div
                    key={plan._id || plan.id || plan.name}
                    className={`h-full rounded-2xl p-4 sm:p-5 flex flex-col justify-between transition-all duration-200 bg-white ${isCurrent
                      ? "border-2 border-[#115B9D] shadow-md shadow-[#115B9D]/10"
                      : "border border-slate-200 shadow-2xs hover:border-blue-300 hover:shadow-sm"
                      }`}
                  >
                    <div>
                      {/* Top Header: Plan Name & Badge */}
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight truncate">
                          {plan.name}
                        </h3>
                        {isCurrent ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-blue-50 text-[#115B9D] border border-blue-100 shrink-0">
                            <Sparkles size={11} />
                            Upgrade
                          </span>
                        )}
                      </div>

                      {/* Tagline */}
                      <p className="text-xs text-slate-500 line-clamp-1 mb-3">
                        {plan.tagline || plan.description || "Scalable digital equipment quoting tier"}
                      </p>

                      {/* Price & Free Trial Days */}
                      <div className="flex items-baseline justify-between gap-2 mb-3 pb-3 border-b border-slate-100">
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                            ${price}
                          </span>
                          <span className="text-xs font-semibold text-slate-500">/ month</span>
                        </div>
                        {freeDays > 0 && (
                          <span className="inline-flex items-center text-[10px] sm:text-[11px] font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-md shrink-0">
                            {freeDays} Days Free Trial
                          </span>
                        )}
                      </div>

                      {/* Horizontal Compact Quota Strip */}
                      <div className="grid grid-cols-3 gap-1 py-2 px-2.5 rounded-xl bg-slate-50 border border-slate-100 text-center mb-3 text-xs">
                        <div>
                          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Seats</span>
                          <span className="block font-bold text-slate-800 text-xs mt-0.5">{seats}</span>
                        </div>
                        <div className="border-x border-slate-200/70">
                          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Quotes</span>
                          <span className="block font-bold text-slate-800 text-xs mt-0.5">{quotes}/mo</span>
                        </div>
                        <div>
                          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">SKUs</span>
                          <span className="block font-bold text-slate-800 text-xs mt-0.5">{skus}</span>
                        </div>
                      </div>

                      {/* Capabilities Checklist */}
                      <div className="space-y-1.5 mb-4 text-xs">
                        {features.slice(0, 3).map((feat, idx) => (
                          <div key={idx} className="flex items-center gap-1.5 text-slate-600">
                            <Check size={13} className="text-emerald-600 shrink-0" strokeWidth={2.5} />
                            <span className="truncate">{feat}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Action Button */}
                    <div className="pt-2">
                      {isCurrent ? (
                        <div className="w-full py-2.5 px-3 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200 font-bold text-xs flex items-center justify-center gap-1.5 select-none">
                          <CheckCircle2 size={14} className="text-emerald-600" />
                          <span>Current Plan</span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedPlan(plan);
                            setChangeNotes(
                              `Requesting upgrade to ${plan.name} for increased team seats and catalog capacity.`
                            );
                          }}
                          className="w-full py-2.5 px-3 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white font-bold text-xs shadow-xs hover:shadow transition-all flex items-center justify-center gap-1.5 group cursor-pointer"
                        >
                          <span>Switch to {plan.name}</span>
                          <ArrowRight
                            size={13}
                            className="transition-transform group-hover:translate-x-0.5"
                          />
                        </button>
                      )}
                    </div>
                  </div>
                );
              };

              const itemsPerView = windowWidth < 640 ? 1 : windowWidth < 1024 ? 2 : 3;
              const isSliderNeeded = availablePlans.length > 3;
              const maxSlideIndex = Math.max(0, availablePlans.length - itemsPerView);

              return (
                <>
                  {!isSliderNeeded ? (
                    /* When 3 or fewer plans: show on one screen without slider or arrows */
                    <div className="flex flex-wrap items-stretch justify-center gap-5 max-w-7xl mx-auto pb-4">
                      {availablePlans.map((plan, index) => (
                        <div
                          key={plan._id || plan.id || `${plan.name}-${index}`}
                          className="w-full sm:w-[320px] max-w-[340px]"
                        >
                          {renderCard(plan)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* When more than 3 plans: show exactly 3 cards per screen in a smooth slider */
                    <div className="relative max-w-7xl mx-auto pb-6 px-8 sm:px-12">
                      {/* Left Arrow Button (Vertically Centered on Left) */}
                      {slideIndex > 0 && (
                        <button
                          type="button"
                          onClick={() => setSlideIndex((prev) => Math.max(0, prev - 1))}
                          className="absolute left-0 sm:left-1 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md hover:shadow-lg flex items-center justify-center text-slate-700 hover:text-[#115B9D] hover:scale-105 active:scale-95 transition-all cursor-pointer"
                          title="Previous Plan"
                        >
                          <ChevronLeft size={20} />
                        </button>
                      )}

                      {/* Viewport Window: strictly 3 cards on screen, never wraps */}
                      <div className="overflow-hidden w-full py-1">
                        <div
                          className="flex flex-nowrap transition-transform duration-300 ease-out"
                          style={{
                            transform: `translateX(-${slideIndex * (100 / itemsPerView)}%)`,
                          }}
                        >
                          {availablePlans.map((plan, index) => (
                            <div
                              key={plan._id || plan.id || `${plan.name}-${index}`}
                              style={{ width: `${100 / itemsPerView}%`, flexShrink: 0 }}
                              className="px-2.5 box-border"
                            >
                              {renderCard(plan)}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Right Arrow Button (Vertically Centered on Right) */}
                      {slideIndex < maxSlideIndex && (
                        <button
                          type="button"
                          onClick={() => setSlideIndex((prev) => Math.min(maxSlideIndex, prev + 1))}
                          className="absolute right-0 sm:right-1 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md hover:shadow-lg flex items-center justify-center text-slate-700 hover:text-[#115B9D] hover:scale-105 active:scale-95 transition-all cursor-pointer"
                          title="Next Plan"
                        >
                          <ChevronRight size={20} />
                        </button>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ======================================================== */}
      {/* MODAL: SUBMIT CHANGE REQUEST */}
      {/* ======================================================== */}
      {selectedPlan && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[10000] flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div
            className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  Request Plan Change
                </h3>
                <p className="text-xs text-slate-500">
                  Transitioning to {selectedPlan.name}
                </p>
              </div>

              <button
                onClick={() => setSelectedPlan(null)}
                className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmitChangeRequest} className="p-6 space-y-4">
              <div className="p-3.5 rounded-2xl bg-blue-50/80 border border-blue-100 flex items-center justify-between text-xs">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase">Current:</span>
                  <p className="font-bold text-slate-800">{currentPlan.name || "Desktop Pro"}</p>
                </div>
                <ArrowRight size={14} className="text-[#115B9D]" />
                <div className="text-right">
                  <span className="text-[10px] font-bold text-[#115B9D] uppercase">Requested:</span>
                  <p className="font-black text-slate-900">{selectedPlan.name}</p>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-800 block mb-1">
                  Notes &amp; Justification for Request
                </label>
                <textarea
                  rows={3}
                  value={changeNotes}
                  onChange={(e) => setChangeNotes(e.target.value)}
                  placeholder="Reason for change..."
                  className="w-full text-xs sm:text-sm p-3.5 rounded-2xl border border-slate-200 focus:border-[#115B9D] focus:ring-2 focus:ring-[#115B9D]/20 outline-none transition-all placeholder:text-slate-400 resize-none text-slate-800"
                  required
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setSelectedPlan(null)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs sm:text-sm font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-gradient-to-r from-[#115B9D] to-[#1E88E5] text-white text-xs sm:text-sm font-bold shadow-md shadow-[#115B9D]/20 transition-all disabled:opacity-50"
                >
                  <Send size={14} />
                  <span>{submitting ? "Submitting..." : "Submit Request"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}
